import { useState } from 'react';
import {
  useAgentConnectors,
  useAttachAgentConnector,
  useConnectors,
  useDetachAgentConnector,
} from '@/lib/api/connectors';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';

interface AgentConnectorsPanelProps {
  agentId: string;
}

/** Linked-connectors selector for an agent: list, attach, detach (AC-5). */
export function AgentConnectorsPanel({ agentId }: AgentConnectorsPanelProps) {
  const linked = useAgentConnectors(agentId);
  const allConnectors = useConnectors();
  const attach = useAttachAgentConnector(agentId);
  const detach = useDetachAgentConnector(agentId);
  const [selected, setSelected] = useState('');

  const linkedIds = new Set((linked.data ?? []).map((c) => c.id));
  const available = (allConnectors.data ?? []).filter((c) => !linkedIds.has(c.id));

  function handleAttach() {
    if (!selected) return;
    attach.mutate(selected, { onSuccess: () => setSelected('') });
  }

  return (
    <Card title="Connectors" description="External APIs this agent can call during chat.">
      {linked.isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (linked.data ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">No connectors linked yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-gray-100">
          {linked.data?.map((connector) => (
            <li
              key={connector.id}
              className="flex items-center justify-between gap-3 py-2"
              data-testid="agent-linked-connector"
            >
              <span className="truncate text-sm text-gray-900">{connector.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => detach.mutate(connector.id)}
                disabled={detach.isPending}
                data-testid="detach-connector-button"
              >
                Detach
              </Button>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 && (
        <div className="flex items-end gap-2">
          <Select
            label="Link a connector"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="attach-connector-select"
          >
            <option value="">Select a connector...</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            onClick={handleAttach}
            disabled={!selected || attach.isPending}
            data-testid="attach-connector-button"
          >
            Attach
          </Button>
        </div>
      )}
    </Card>
  );
}
